import { useState, useEffect } from 'react';
import { useI18n } from '../../i18n';
import { useSEO } from '../../hooks/useSEO';

interface Match {
    fullMatch: string;
    groups: string[];
    index: number;
}

export default function RegexTester() {
    const { t } = useI18n();
    const [pattern, setPattern] = useState('');
    const [flags, setFlags] = useState({
        g: true,
        i: false,
        m: false,
        s: false,
    });
    const [testString, setTestString] = useState('');
    const [matches, setMatches] = useState<Match[]>([]);
    const [error, setError] = useState('');

    useSEO({
        title: t.tools.regex.name,
        description: t.tools.regex.description,
        keywords: ['regex', 'regular expression', 'pattern matching', 'test', 'validator'],
    });

    useEffect(() => {
        testRegex();
    }, [pattern, flags, testString]);

    const testRegex = () => {
        if (!pattern || !testString) {
            setMatches([]);
            setError('');
            return;
        }

        try {
            const flagString = Object.entries(flags)
                .filter(([_, enabled]) => enabled)
                .map(([flag]) => flag)
                .join('');

            const regex = new RegExp(pattern, flagString);
            const foundMatches: Match[] = [];

            if (flags.g) {
                // Global flag: find all matches
                let match;
                while ((match = regex.exec(testString)) !== null) {
                    foundMatches.push({
                        fullMatch: match[0],
                        groups: match.slice(1),
                        index: match.index,
                    });
                    // Prevent infinite loop on zero-length matches
                    if (match.index === regex.lastIndex) {
                        regex.lastIndex++;
                    }
                }
            } else {
                // Non-global: find first match only
                const match = regex.exec(testString);
                if (match) {
                    foundMatches.push({
                        fullMatch: match[0],
                        groups: match.slice(1),
                        index: match.index,
                    });
                }
            }

            setMatches(foundMatches);
            setError('');
        } catch (err) {
            setError(err instanceof Error ? err.message : t.tools.regex.invalidRegex);
            setMatches([]);
        }
    };

    const toggleFlag = (flag: keyof typeof flags) => {
        setFlags(prev => ({ ...prev, [flag]: !prev[flag] }));
    };

    const highlightMatches = () => {
        if (!testString || matches.length === 0) {
            return testString;
        }

        const parts: JSX.Element[] = [];
        let lastIndex = 0;

        matches.forEach((match, i) => {
            // Add text before match
            if (match.index > lastIndex) {
                parts.push(
                    <span key={`text-${i}`}>
                        {testString.substring(lastIndex, match.index)}
                    </span>
                );
            }

            // Add highlighted match
            parts.push(
                <span
                    key={`match-${i}`}
                    className="bg-yellow-200 border border-yellow-400 rounded px-1"
                    title={`Match ${i + 1}`}
                >
                    {match.fullMatch}
                </span>
            );

            lastIndex = match.index + match.fullMatch.length;
        });

        // Add remaining text
        if (lastIndex < testString.length) {
            parts.push(
                <span key="text-end">
                    {testString.substring(lastIndex)}
                </span>
            );
        }

        return <>{parts}</>;
    };

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {t.tools.regex.name}
                </h1>
                <p className="text-gray-600">{t.tools.regex.description}</p>
            </div>

            <div className="space-y-6">
                {/* Pattern Input */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.tools.regex.pattern}
                    </label>
                    <input
                        type="text"
                        value={pattern}
                        onChange={(e) => setPattern(e.target.value)}
                        placeholder="^[a-z]+\d{2,4}$"
                        className="input-field font-mono"
                    />
                </div>

                {/* Flags */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.tools.regex.flags}
                    </label>
                    <div className="flex flex-wrap gap-3">
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={flags.g}
                                onChange={() => toggleFlag('g')}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm">
                                <code className="bg-gray-100 px-1 rounded">g</code> - {t.tools.regex.globalFlag}
                            </span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={flags.i}
                                onChange={() => toggleFlag('i')}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm">
                                <code className="bg-gray-100 px-1 rounded">i</code> - {t.tools.regex.caseInsensitive}
                            </span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={flags.m}
                                onChange={() => toggleFlag('m')}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm">
                                <code className="bg-gray-100 px-1 rounded">m</code> - {t.tools.regex.multiline}
                            </span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={flags.s}
                                onChange={() => toggleFlag('s')}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm">
                                <code className="bg-gray-100 px-1 rounded">s</code> - {t.tools.regex.dotAll}
                            </span>
                        </label>
                    </div>
                </div>

                {/* Test String */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.tools.regex.testString}
                    </label>
                    <textarea
                        value={testString}
                        onChange={(e) => setTestString(e.target.value)}
                        placeholder="Enter text to test against the regex pattern..."
                        className="input-field font-mono"
                        rows={6}
                    />
                </div>

                {/* Error Display */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-700 text-sm">
                            <span className="font-semibold">{t.tools.jsonFormatter.error}:</span> {error}
                        </p>
                    </div>
                )}

                {/* Highlighted Text */}
                {testString && !error && (
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">
                            {t.tools.regex.testString} ({matches.length} {matches.length === 1 ? 'match' : 'matches'})
                        </h2>
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <pre className="whitespace-pre-wrap break-words font-mono text-sm">
                                {highlightMatches()}
                            </pre>
                        </div>
                    </div>
                )}

                {/* Matches Display */}
                {matches.length > 0 && (
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">
                            {t.tools.regex.matches} ({matches.length})
                        </h2>
                        <div className="space-y-3">
                            {matches.map((match, index) => (
                                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-semibold text-primary-600">
                                            Match {index + 1}
                                        </span>
                                        <span className="text-sm text-gray-500">
                                            Position: {match.index}
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        <div>
                                            <span className="text-sm font-medium text-gray-700">Full Match: </span>
                                            <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                                                {match.fullMatch}
                                            </code>
                                        </div>
                                        {match.groups.length > 0 && (
                                            <div>
                                                <span className="text-sm font-medium text-gray-700">
                                                    Capture Groups ({match.groups.length}):
                                                </span>
                                                <div className="mt-1 space-y-1">
                                                    {match.groups.map((group, i) => (
                                                        <div key={i} className="ml-4">
                                                            <span className="text-sm text-gray-600">Group {i + 1}: </span>
                                                            <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                                                                {group || '(empty)'}
                                                            </code>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* No Matches */}
                {pattern && testString && !error && matches.length === 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                        <p className="text-gray-600">{t.tools.regex.noMatches}</p>
                    </div>
                )}

                {/* Common Regex Patterns */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <h3 className="font-semibold text-primary-900 mb-3">
                        💡 Common Patterns
                    </h3>
                    <div className="space-y-2 text-sm">
                        <div className="flex items-start space-x-2">
                            <code className="bg-white px-2 py-1 rounded flex-shrink-0">^\w+@\w+\.\w+$</code>
                            <span className="text-primary-800">- Email address</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <code className="bg-white px-2 py-1 rounded flex-shrink-0">^https?://</code>
                            <span className="text-primary-800">- URL protocol</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <code className="bg-white px-2 py-1 rounded flex-shrink-0">^\d{3}-\d{3}-\d{4}$</code>
                            <span className="text-primary-800">- Phone number (123-456-7890)</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <code className="bg-white px-2 py-1 rounded flex-shrink-0">^#[0-9A-Fa-f]{6}$</code>
                            <span className="text-primary-800">- HEX color code</span>
                        </div>
                        <div className="flex items-start space-x-2">
                            <code className="bg-white px-2 py-1 rounded flex-shrink-0">^\d{4}-\d{2}-\d{2}$</code>
                            <span className="text-primary-800">- Date (YYYY-MM-DD)</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
